import { useContext, useMemo, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { isEqual, compact, map, findKey, split } from "lodash"
import MetricsSelector from "components/custom-metrics-selector"
import { useMetricsSelectorAnalytics } from "components/custom-metrics-selector/hooks/use-metrics-selector-analytics"

export default function MetricsSelectorWrapper({
  context,
  modelMetadata,
  templateMetadata,
  selectedMetrics,
  availableMetrics,
  flattenedMetrics,
  setSelectedMetrics,
}) {
  const assetViewContext = useContext(context)
  const {
    setView,
    view,
    deleteTemplate,
    saveTemplate,
    setShowMetricsSelector,
    showMetricsSelector,
  } = assetViewContext
  const analytics = useMetricsSelectorAnalytics(assetViewContext)

  const { topFactors: modelTopFactors } = modelMetadata

  const topFactors = useMemo(() => compact(map(modelTopFactors, (factorId) => findKey(flattenedMetrics, ({ dataPath }) => {
    const parts = split(dataPath, ".")
    return factorId === parts[parts.length - 1]
  }))), [modelTopFactors, flattenedMetrics])

  const currentMetrics = useMemo(() => templateMetadata?.config.metrics || topFactors, [templateMetadata, topFactors])
  useEffect(() => {
    // Avoid setting selectedMetrics if they are equivalent to the current metrics (prevents infinite re-renders)
    if (!isEqual(selectedMetrics, currentMetrics)) {
      setSelectedMetrics(currentMetrics)
    }
  }, [currentMetrics, selectedMetrics, setSelectedMetrics])

  const handleToggle = useCallback(() => {
    if (showMetricsSelector) {
      analytics.trackClosed(templateMetadata)
    } else {
      analytics.trackOpened(templateMetadata)
    }
    setShowMetricsSelector(!showMetricsSelector)
  }, [analytics, showMetricsSelector, setShowMetricsSelector, templateMetadata])

  const handleSaveMetricsTemplate = useCallback(async (template) => {
    const metricsCount = Object.values(template.metrics || {}).filter(Boolean).length

    if (templateMetadata?.id) {
      analytics.trackViewUpdated(templateMetadata.id, template.name, metricsCount)
    } else {
      analytics.trackViewSaved(template.name, metricsCount)
    }

    const savedTemplate = await saveTemplate({ id: templateMetadata?.id, ...template })
    if (savedTemplate && view !== savedTemplate.id) {
      setView(savedTemplate.id)
    }
  }, [analytics, saveTemplate, setView, templateMetadata, view])

  const handleDeleteCustomTemplate = useCallback(async (template) => {
    analytics.trackViewDeleted(template.id, template.name)
    await deleteTemplate(template)
    setView("exposure")
  }, [analytics, deleteTemplate, setView])

  return <MetricsSelector
    isOpen={showMetricsSelector}
    toggle={handleToggle}
    setSelectedMetrics={(metrics) => setSelectedMetrics(metrics)}
    includeMetricTypes
    defaultSelectedMetrics={topFactors}
    availableMetrics={availableMetrics}
    saveCustomTemplate={handleSaveMetricsTemplate}
    deleteCustomTemplate={handleDeleteCustomTemplate}
    customTemplate={templateMetadata}
    analytics={analytics}
  />
}

MetricsSelectorWrapper.propTypes = {
  context: PropTypes.object.isRequired,
  modelMetadata: PropTypes.object.isRequired,
  templateMetadata: PropTypes.object.isRequired,
  availableMetrics: PropTypes.array.isRequired,
  flattenedMetrics: PropTypes.array.isRequired,
  setSelectedMetrics: PropTypes.func.isRequired,
  selectedMetrics: PropTypes.array.isRequired,
}
